import { useIonActionSheet } from '@ionic/react';

import { Icon } from '~/components/ui/Icon';
import { useNetworkStatus } from '~/hooks/useNetworkStatus';

/**
 * Barra discreta que aparece quando há algo pra comunicar, em ordem de prioridade:
 *  - offline                        → "Sem conexão · X pendentes"
 *  - falhas terminais (online)      → "X nota não enviada · toque para resolver"
 *  - fila drenando (online)         → "Sincronizando..."
 *  - nada pendente                  → some
 *
 * A falha terminal é o estado em que o drain desistiu após esgotar as tentativas.
 * Fica separada de "pendente" de propósito: pendente ainda vai tentar sozinho; falha
 * exige decisão do usuário (tentar de novo ou descartar), então não pode se disfarçar
 * de pendente nem sumir sozinha.
 *
 * Renderizado no topo de cada rota protegida via App.tsx, sticky para sobreviver ao
 * scroll interno do IonContent.
 */
export function NetworkBanner(): JSX.Element | null {
  const { isOnline, isSyncing, pendingCount, failedCount, retryFailed, discardFailed } =
    useNetworkStatus();
  const [presentActionSheet] = useIonActionSheet();

  const isOfflineWithQueue = !isOnline;
  const hasFailed = isOnline && failedCount > 0;
  const isDraining = isOnline && !hasFailed && (isSyncing || pendingCount > 0);

  if (!isOfflineWithQueue && !hasFailed && !isDraining) return null;

  const openFailedMenu = (): void => {
    presentActionSheet({
      header:
        failedCount === 1 ? '1 nota não foi enviada' : `${failedCount} notas não foram enviadas`,
      buttons: [
        { text: 'Tentar novamente', handler: () => void retryFailed() },
        { text: 'Descartar', role: 'destructive', handler: () => void discardFailed() },
        { text: 'Cancelar', role: 'cancel' },
      ],
    });
  };

  const tone = isOfflineWithQueue ? 'warn' : hasFailed ? 'negative' : 'brand';
  const bg =
    tone === 'warn'
      ? 'var(--rx-warn-tint)'
      : tone === 'negative'
        ? 'var(--rx-negative-tint)'
        : 'var(--rx-brand-tint)';
  const fg =
    tone === 'warn'
      ? 'var(--rx-warn)'
      : tone === 'negative'
        ? 'var(--rx-negative)'
        : 'var(--rx-brand)';
  const iconName = tone === 'brand' ? 'refresh' : 'warn';

  const label = isOfflineWithQueue
    ? pendingCount > 0
      ? `Sem conexão · ${pendingCount} ${pendingCount === 1 ? 'pendente' : 'pendentes'}`
      : 'Sem conexão · trabalhando offline'
    : hasFailed
      ? `${failedCount} ${failedCount === 1 ? 'nota não enviada' : 'notas não enviadas'} · toque para resolver`
      : isSyncing
        ? `Sincronizando ${pendingCount > 0 ? `${pendingCount} ` : ''}${
            pendingCount === 1 ? 'nota' : 'notas'
          }…`
        : `${pendingCount} ${pendingCount === 1 ? 'nota pendente' : 'notas pendentes'}`;

  const Wrapper = hasFailed ? 'button' : 'output';

  return (
    <Wrapper
      type={hasFailed ? 'button' : undefined}
      aria-live="polite"
      onClick={hasFailed ? openFailedMenu : undefined}
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 16px',
        background: bg,
        color: fg,
        fontSize: 12,
        fontWeight: 500,
        letterSpacing: '-0.1px',
        border: 'none',
        borderBottom: '0.5px solid var(--rx-border)',
        backdropFilter: 'saturate(140%) blur(6px)',
        textAlign: 'left',
        cursor: hasFailed ? 'pointer' : 'default',
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 18,
          height: 18,
          animation: isDraining ? 'rx-spin 1s linear infinite' : undefined,
        }}
      >
        <Icon name={iconName} size={14} color={fg} />
      </span>
      <span style={{ flex: 1 }}>{label}</span>

      <style>{'@keyframes rx-spin { to { transform: rotate(360deg); } }'}</style>
    </Wrapper>
  );
}
